import type { PermissionCode } from './permissionTypes';

/**
 * What a gated surface asks for: one permission code, or several of which **any** suffices.
 *
 * <p>The array form exists because the platform's read/write tiers are not always a wildcard family.
 * The staff directory is the motivating case: the BFF accepts either `employees.view` (the read tier
 * added by plan-auth-001 Phase 3.1) **or** `employees.manage` on `GET /api/v1/employees` — a principal
 * resolved from the legacy role mapping holds only the manage code, and refusing them the screen the
 * server would happily serve is a worse failure than the extra code in the list. Mirroring the server's
 * own "manage implies view" here keeps the two in step (`EmployeeControllerImpl.requireViewTenant`).
 *
 * <p>ANY, never ALL: a requirement is satisfied by the strongest code the caller happens to hold. When
 * a surface genuinely needs two unrelated permissions at once, gate it twice rather than widening this.
 */
export type PermissionRequirement = PermissionCode | PermissionCode[];

/** The codes a requirement accepts, any one of which grants it. */
export function permissionCodesOf(requirement: PermissionRequirement): PermissionCode[] {
  return Array.isArray(requirement) ? requirement : [requirement];
}

/** True when the caller holds at least one of the codes a requirement accepts. */
export function satisfiesPermission(
  requirement: PermissionRequirement,
  hasPermission: (permission: PermissionCode) => boolean,
): boolean {
  return permissionCodesOf(requirement).some(hasPermission);
}

/**
 * True when the session holds **any** code inside a namespace, e.g. `"settings."`.
 *
 * <p>Answers a question {@link satisfiesPermission} structurally cannot: "can this user see *any*
 * settings domain?" — where the caller does not know, and should not have to enumerate, the ~30 codes
 * that would qualify. Wildcards are honoured from both directions: a `*` or `settings.*` grant matches
 * the namespace, and so does a single concrete `settings.inventory.view`.
 *
 * <p>Use it only for that shape. Gating a *screen* on "something under this prefix" would be far too
 * loose — screens name the code they need.
 */
export function grantsAnythingUnder(granted: PermissionCode[], namespace: string): boolean {
  // Correctness depends on the namespace naming a whole segment. Left to the caller, `"settings"`
  // would quietly also match a future `settingsomething.view`, and `""` would match every code the
  // session holds — the two ways this function could hand out access it was never asked for. Both are
  // one forgotten character away, so normalise rather than document and hope.
  if (!namespace) {
    return false;
  }
  const prefix = namespace.endsWith('.') ? namespace : `${namespace}.`;

  return granted.some((code) => {
    if (code === '*') {
      return true;
    }
    if (code.endsWith('.*')) {
      // Two prefix classes intersect exactly when one is a prefix of the other, so the test runs both
      // ways: `settings.*` covers `settings.`, and so does the narrower `settings.orders.*`. A
      // `catalog.*` grant covers neither.
      const granted = code.slice(0, -1);
      return prefix.startsWith(granted) || granted.startsWith(prefix);
    }
    return code.startsWith(prefix);
  });
}
